import { useMutateShareChat } from '@/hooks/mutation/useMutateShareChat';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { toast } from '@heidi/design-system/toast';
import type { SharePreviewData } from '@heidi/evidence';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

export interface ShareDebugInfo {
  timestamp: string;
  chatId: string;
  userEmail: string | null;
  environment: string;
  apiBaseUrl: string;
  errorMessage: string;
  errorStatus: number | null;
  errorResponse: string | null;
}

interface UseShareEvidenceChatParams {
  chatId: string;
  onShareSuccess?: (data: SharePreviewData) => void;
}

export function useShareEvidenceChat({
  chatId,
  onShareSuccess,
}: UseShareEvidenceChatParams) {
  const intl = useIntl();
  const { trackShareLinkCopied, trackShareSocialClicked } = useEvidenceAnalytics();
  const [shareData, setShareData] = useState<SharePreviewData | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [debugInfo, setDebugInfo] = useState<ShareDebugInfo | null>(null);
  const { data: userEmail } = useQueryUser((user) => user.email);

  const { mutateAsync: shareChatMutation, isPending: isCreatingShare } =
    useMutateShareChat();

  const createShare = useCallback(async () => {
    // Clear previous debug info
    setDebugInfo(null);

    try {
      const result = await shareChatMutation(chatId);
      const data: SharePreviewData = {
        shareToken: result.shareToken,
        shareUrl: result.shareUrl,
        previewQuestion: result.previewQuestion,
        previewResponse: result.previewResponse,
        chatTitle: result.chatTitle,
      };
      setShareData(data);
      onShareSuccess?.(data);
      return data;
    } catch (error) {
      // Capture debug info for troubleshooting
      const axiosError = error as {
        response?: { status?: number; data?: unknown };
        message?: string;
      };
      const debug: ShareDebugInfo = {
        timestamp: new Date().toISOString(),
        chatId,
        userEmail: userEmail ?? null,
        environment: process.env.NEXT_PUBLIC_ENVIRONMENT ?? 'unknown',
        apiBaseUrl: process.env.NEXT_PUBLIC_API_BASE_URL ?? 'unknown',
        errorMessage: axiosError.message ?? 'Unknown error',
        errorStatus: axiosError.response?.status ?? null,
        errorResponse: axiosError.response?.data
          ? JSON.stringify(axiosError.response.data)
          : null,
      };
      setDebugInfo(debug);

      toast({
        tone: 'destructive',
        title: intl.formatMessage({
          defaultMessage: 'Failed to create share link',
          id: 'N6omPQO2Zj',
        }),
      });
      return null;
    }
  }, [chatId, shareChatMutation, onShareSuccess, userEmail, intl]);

  const copyLink = useCallback(async () => {
    if (!shareData?.shareUrl) {
      // Create share first if not already created
      const data = await createShare();
      if (!data) return false;

      try {
        await navigator.clipboard.writeText(data.shareUrl);
        setIsCopied(true);
        trackShareLinkCopied(chatId);
        setTimeout(() => setIsCopied(false), 2000);
        return true;
      } catch {
        toast({
          tone: 'destructive',
          title: intl.formatMessage({
            defaultMessage: 'Failed to copy link',
            id: 'ruCwQMVFzJ',
          }),
          duration: 3000,
        });
        return false;
      }
    }

    try {
      await navigator.clipboard.writeText(shareData.shareUrl);
      setIsCopied(true);
      trackShareLinkCopied(chatId);
      setTimeout(() => setIsCopied(false), 2000);
      return true;
    } catch {
      toast({
        tone: 'destructive',
        title: intl.formatMessage({
          defaultMessage: 'Failed to copy link',
          id: 'ruCwQMVFzJ',
        }),
        duration: 3000,
      });
      return false;
    }
  }, [shareData, createShare, chatId, trackShareLinkCopied, intl]);

  const openSocialShare = useCallback(
    async (platform: 'linkedin' | 'x' | 'reddit' | 'whatsapp') => {
      let data = shareData;
      if (!data) {
        data = await createShare();
        if (!data) return;
      }

      const encodedUrl = encodeURIComponent(data.shareUrl);
      const encodedTitle = encodeURIComponent(
        `Check out this medical evidence from Heidi: ${data.chatTitle}`
      );

      let socialUrl: string;
      switch (platform) {
        case 'linkedin':
          socialUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
          break;
        case 'x':
          socialUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
          break;
        case 'reddit':
          socialUrl = `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`;
          break;
        case 'whatsapp':
          socialUrl = `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`;
          break;
      }

      trackShareSocialClicked(chatId, platform);
      window.open(socialUrl, '_blank', 'noopener,noreferrer');
    },
    [shareData, createShare, chatId, trackShareSocialClicked]
  );

  const copyDebugInfo = useCallback(async () => {
    if (!debugInfo) return false;
    try {
      const debugText = `Share Debug Info:
─────────────────────────────────
Timestamp: ${debugInfo.timestamp}
Chat ID: ${debugInfo.chatId}
User Email: ${debugInfo.userEmail ?? 'Not available'}
Environment: ${debugInfo.environment}
API Base URL: ${debugInfo.apiBaseUrl}
Error Status: ${debugInfo.errorStatus ?? 'N/A'}
Error Message: ${debugInfo.errorMessage}
Error Response: ${debugInfo.errorResponse ?? 'N/A'}
─────────────────────────────────`;
      await navigator.clipboard.writeText(debugText);
      toast({
        tone: 'success',
        title: intl.formatMessage({
          defaultMessage: 'Debug info copied',
          id: 'jc5TP5uswn',
        }),
        description: intl.formatMessage({
          defaultMessage: 'Paste this to share with support',
          id: 'XYqIv2iYNT',
        }),
        duration: 3000,
      });
      return true;
    } catch {
      return false;
    }
  }, [debugInfo, intl]);

  return {
    shareData,
    isCreatingShare,
    isCopied,
    debugInfo,
    createShare,
    copyLink,
    openSocialShare,
    copyDebugInfo,
  };
}
